import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { Info, TriangleAlert } from "lucide-react";

import { UIActionModal, UICallout, UISelect } from "../../../../ui";
import { clx } from "../../../../../utils/Helpers";

const isSameId = (first, second) =>
  first !== undefined &&
  first !== null &&
  second !== undefined &&
  second !== null &&
  String(first) === String(second);

const getAssignedZone = (applicant, zoneOptions = []) =>
  applicant?.zone ||
  zoneOptions.find((option) => isSameId(option.supervisorId, applicant?.userId))
    ?.name ||
  "";

const getApplicantDescription = (applicant) =>
  [
    `#${applicant.userId || applicant.requestId || applicant.id}`,
    applicant.requestedRole,
    applicant.shift,
  ]
    .filter(Boolean)
    .join(" - ");

const ChangeRoleModal = ({
  open,
  applicants,
  zoneOptions,
  roleOptions,
  shiftOptions,
  copy,
  onClose,
  onConfirm,
}) => {
  const [role, setRole] = useState(null);
  const [shift, setShift] = useState(null);
  const supervisors = useMemo(
    () => applicants.filter((applicant) => applicant.isSupervisor),
    [applicants],
  );
  const isBulk = applicants.length > 1;
  const isMixed = supervisors.length > 0 && supervisors.length < applicants.length;
  const primaryApplicant = applicants[0];
  const availableShiftOptions = useMemo(
    () =>
      role?.id
        ? shiftOptions.filter((item) => String(item.roleId) === String(role.id))
        : [],
    [role, shiftOptions],
  );

  useEffect(() => {
    if (!open) return;
    setRole(null);
    setShift(null);
  }, [open]);

  const showSingleSupervisorWarning =
    !isBulk && Boolean(primaryApplicant?.isSupervisor);
  const selectedSummary = copy.selectedTemplate.replace(
    "{count}",
    applicants.length,
  );
  const modalMessage = (
    isMixed
      ? copy.mixedMessage
      : isBulk
        ? copy.bulkMessage
        : copy.singleMessage
  ).replace("{count}", applicants.length);
  const overrideDescription = copy.overrideDescription.replace(
    "{count}",
    applicants.length,
  );
  const hasSupervisors = supervisors.length > 0;
  const overrideTitle = hasSupervisors
    ? copy.overrideSupervisorTitle
    : copy.overrideTitle;
  const assignedZone = getAssignedZone(primaryApplicant, zoneOptions);
  const demotionDescription = (
    assignedZone
      ? copy.singleDemotionDescription
      : copy.singleUnassignedDemotionDescription
  )
    .replace("{name}", primaryApplicant?.name || "")
    .replace("{zone}", assignedZone);
  const unassignZoneMessage = copy.unassignZones.replace("{zone}", assignedZone);
  const zonesNeedSupervisorMessage = copy.zonesNeedSupervisor.replace(
    "{zone}",
    assignedZone,
  );

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={copy.title}
      maxWidth="sm"
      PaperProps={{ className: "event-applicants-change-role-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={copy.cancel}
          confirmLabel={copy.confirm}
          confirmDisabled={!role?.id || !shift?.id}
          onCancel={onClose}
          onConfirm={() => onConfirm({ role, shift })}
        />
      }
    >
      <UIActionModal.Divider />
      <Typography
        className={clx(
          "event-applicants-change-role-modal__message",
          !isBulk && "event-applicants-change-role-modal__message--muted",
        )}
      >
        {modalMessage}
      </Typography>
      {isBulk ? (
        <Box className="event-applicants-change-role-modal__summary">
          <Typography className="event-applicants-change-role-modal__summary-title">
            {selectedSummary}
          </Typography>
          <Typography className="event-applicants-change-role-modal__summary-subtitle">
            {copy.mixOfRolesAndShifts}
          </Typography>
        </Box>
      ) : primaryApplicant ? (
        <UIActionModal.UserInfo
          name={primaryApplicant.name}
          image={primaryApplicant.image}
          description={getApplicantDescription(primaryApplicant)}
        />
      ) : null}

      {showSingleSupervisorWarning && (
        <UICallout
          variant="error"
          className="event-applicants-change-role-modal__warning event-applicants-change-role-modal__warning--demotion"
          icon={
            <Info
              size={20}
              strokeWidth={2}
              color="var(--color-destructive-default, #dc2626)"
              className="ui-callout__icon"
            />
          }
          title={copy.demotionTitle}
          description={
            <Box className="event-applicants-change-role-modal__warning-content">
              <Typography component="span">
                {demotionDescription}
              </Typography>
              <ul>
                <li>{copy.removeSupervisorStatus}</li>
                {assignedZone && <li>{unassignZoneMessage}</li>}
                {assignedZone && <li>{zonesNeedSupervisorMessage}</li>}
              </ul>
            </Box>
          }
          hasBorder={false}
        />
      )}

      <UISelect
        label={copy.newRole}
        placeholder={copy.selectRole}
        value={role}
        options={roleOptions}
        onChange={(value) => {
          setRole(value);
          setShift(null);
        }}
      />
      <UISelect
        label={copy.newShift}
        placeholder={copy.selectShift}
        value={shift}
        options={availableShiftOptions}
        onChange={setShift}
        disabled={!role?.id}
      />

      {isBulk && (
        <UICallout
          variant="warning"
          className="event-applicants-change-role-modal__warning event-applicants-change-role-modal__warning--override"
          icon={
            <TriangleAlert
              size={20}
              color="var(--color-warning-default, #f59e0b)"
              className="ui-callout__icon event-applicants-change-role-modal__override-icon"
            />
          }
          title={overrideTitle}
          description={overrideDescription}
          hasBorder={false}
        />
      )}
      {isBulk && supervisors.length > 0 && (
        <UICallout
          variant="error"
          className="event-applicants-change-role-modal__warning event-applicants-change-role-modal__warning--bulk-demotion"
          icon={
            <Info
              size={20}
              strokeWidth={2}
              color="var(--color-destructive-default, #dc2626)"
              className="ui-callout__icon"
            />
          }
          title={copy.bulkDemotionWarning}
          hasBorder={false}
        />
      )}
    </UIActionModal>
  );
};

ChangeRoleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  applicants: PropTypes.arrayOf(PropTypes.object),
  zoneOptions: PropTypes.arrayOf(PropTypes.object),
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  shiftOptions: PropTypes.arrayOf(PropTypes.object),
  copy: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ChangeRoleModal.defaultProps = {
  applicants: [],
  zoneOptions: [],
  roleOptions: [],
  shiftOptions: [],
};

export default ChangeRoleModal;
